import NextLink from 'next/link'
import React, { ReactNode, isValidElement, cloneElement } from 'react'

interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  type?: 'outbound' | 'legacy'
  href: string
  children: ReactNode
  className?: string
}

export function Link({ type, href, children, className, ...rest }: LinkProps) {
  const cls = className ?? 'text-brand hover:underline'

  switch (type) {
    case 'outbound':
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={cls}
          {...rest}
        >
          {children}
        </a>
      )
    case 'legacy':
      return (
        <a href={href} className={cls} {...rest}>
          {children}
        </a>
      )
    default: {
      // If any child is a raw <a> element, Next's new Link will throw unless
      // we use legacyBehavior. Detect anchor children and clone them to
      // merge className props so existing usage continues to work.
      let hasAnchor = false

      React.Children.forEach(children, child => {
        if (
          isValidElement(child) &&
          (child.type === 'a' || (child as any).props?.href)
        ) {
          hasAnchor = true
        }
      })

      if (hasAnchor) {
        const cloned = React.Children.map(children, child => {
          if (!isValidElement(child)) return child
          if (child.type === 'a') {
            const childClass = (child as any).props.className ?? ''
            const merged = `${cls} ${childClass}`.trim()
            return cloneElement(
              child as React.ReactElement<any>,
              { className: merged } as any
            )
          }
          return child
        })

        // NextLink expects a single child when using legacyBehavior.
        // If cloning produced multiple children, handle carefully to avoid
        // nested <a> elements. Strategy:
        // - If there are no anchor children, wrap all children in a new <a>.
        // - If there is at least one anchor child, merge other children
        //   into the first anchor (unwrap additional anchors) so we pass a
        //   single <a> element to NextLink without nesting anchors.
        const arr = React.Children.toArray(cloned)
        const anchorItems = arr.filter(
          item => isValidElement(item) && (item as any).type === 'a'
        )

        let singleChild: any

        if (arr.length === 1) {
          singleChild = arr[0]
        } else if (anchorItems.length === 0) {
          // No anchors present: safe to wrap all children in an <a>
          singleChild = (
            <a className={cls} {...(rest as any)}>
              {arr}
            </a>
          )
        } else {
          // At least one anchor present. Use the first anchor as the wrapper
          // and merge other children's content into it (unwrap additional anchors).
          const firstAnchor = anchorItems[0] as React.ReactElement<any>
          const otherContent: React.ReactNode[] = []

          arr.forEach(item => {
            if (!isValidElement(item)) {
              otherContent.push(item)
              return
            }
            if ((item as any).type === 'a') {
              if (item === firstAnchor) return
              // unwrap this anchor: push its children
              otherContent.push((item as any).props.children)
            } else {
              otherContent.push(item)
            }
          })

          const firstChildClass = (firstAnchor.props.className ?? '').toString()
          const mergedClass = `${cls} ${firstChildClass}`.trim()

          singleChild = cloneElement(firstAnchor, {
            className: mergedClass,
            children: [firstAnchor.props.children, ...otherContent]
          } as any)
        }

        return (
          <NextLink href={href} legacyBehavior>
            {singleChild}
          </NextLink>
        )
      }

      // No anchor children: use modern Link behavior and pass className directly
      return (
        // eslint-disable-next-line jsx-a11y/anchor-has-content
        <NextLink href={href} className={cls} {...(rest as any)}>
          {children}
        </NextLink>
      )
    }
  }
}
